import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Heart, Loader2, Mail, ArrowLeft } from "lucide-react";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { lang } = useLang();
  const pl = lang === "pl";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(pl ? "Podaj poprawny email" : "Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password/forgot`, { email, lang });
      setSent(true);
      toast.success(pl ? "Link wysłany (jeśli konto istnieje)" : "Reset link sent (if the account exists)");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="dc-card w-full max-w-md p-8 sm:p-10 shadow-[0_20px_70px_rgba(253,38,122,0.18)]">
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl dc-gradient-bg shadow-[0_12px_40px_rgba(253,38,122,0.4)]">
            <Heart className="h-7 w-7 text-white" fill="white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white">
            <span className="dc-gradient-text">{pl ? "Reset hasła" : "Password reset"}</span>
          </h1>
          <p className="mt-2 text-sm text-[#a8adbd]">
            {pl
              ? "Podaj email — wyślemy link do zresetowania hasła."
              : "Enter your email — we'll send you a reset link."}
          </p>
        </div>

        {sent ? (
          <div data-testid="forgot-sent" className="rounded-2xl border border-[#32d583]/40 bg-[#32d583]/10 p-5 text-center">
            <p className="text-sm text-white">
              {pl
                ? `Jeśli konto z adresem ${email} istnieje, wysłaliśmy na nie link do resetu hasła (ważny 1h). Sprawdź skrzynkę i folder spam.`
                : `If an account with ${email} exists, we sent a password reset link (valid 1h). Check your inbox and spam folder.`}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
                <Mail className="h-3.5 w-3.5" />E-mail
              </label>
              <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3.5 text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/30"
                placeholder="ty@example.com" autoFocus />
            </div>
            <button type="submit" data-testid="forgot-submit-button" disabled={loading || !email}
              className="flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-3.5 font-display font-semibold text-white shadow-[0_10px_30px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span>{loading ? "..." : (pl ? "Wyślij link" : "Send link")}</span>
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-[#a8adbd]">
          <Link to="/login" data-testid="link-to-login" className="inline-flex items-center gap-1 font-semibold dc-gradient-text">
            <ArrowLeft className="h-3.5 w-3.5" />
            {pl ? "Powrót do logowania" : "Back to login"}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
